import { Alert, Card, Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import DashboardNavbar from 'components/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import { useEffect, useState } from 'react';
import { getStreamingConfig } from 'services/streaming-config/get-streaming-config';
import { updateStreamingConfig } from 'services/streaming-config/update-streaming-config';

export default function StreamingConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [streamingProvider, setStreamingProvider] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [bypassQueue, setBypassQueue] = useState(false);
  const [mapType, setMapType] = useState(0);

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getStreamingConfig();
      if (res?.status === 200) {
        const data = res.data || {};
        setStreamingProvider(data.streamingProvider || 'unknown');
        setIsActive(Boolean(data.isActive));
        setBypassQueue(Boolean(data.bypassQueue));
        setMapType(Number.isInteger(data.mapType) ? data.mapType : 0);
      } else {
        setError(res?.response?.data?.message || 'Failed to fetch streaming config');
      }
    } catch (err) {
      setError('Failed to fetch streaming config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const onSave = async () => {
    const parsedMapType = Number(mapType);
    if (!Number.isInteger(parsedMapType) || parsedMapType < 0) {
      setError('Map Type must be a non-negative integer');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const payload = {
        isActive,
        bypassQueue,
        mapType: parsedMapType,
      };
      const res = await updateStreamingConfig(payload);
      if (res?.status === 200 || res?.status === 201) {
        const data = res.data || {};
        setStreamingProvider(data.streamingProvider || streamingProvider);
        setIsActive(Boolean(data.isActive));
        setBypassQueue(Boolean(data.bypassQueue));
        setMapType(Number.isInteger(data.mapType) ? data.mapType : parsedMapType);
        setSuccessMessage('Streaming config updated successfully');
      } else {
        setError(res?.response?.data?.message || 'Failed to update streaming config');
      }
    } catch (err) {
      setError('Failed to update streaming config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3} justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant='h5' fontWeight='bold' mb={2}>
                  Streaming Configuration
                </MDTypography>

                {error && (
                  <MDBox mb={2}>
                    <Alert severity='error'>{error}</Alert>
                  </MDBox>
                )}
                {successMessage && (
                  <MDBox mb={2}>
                    <Alert severity='success'>{successMessage}</Alert>
                  </MDBox>
                )}

                <MDBox mb={2}>
                  <MDTypography variant='button' fontWeight='regular' color='text'>
                    Streaming Provider
                  </MDTypography>
                  <MDBox
                    mt={1}
                    px={1.5}
                    py={1.2}
                    borderRadius='md'
                    sx={({ palette, borders }) => ({
                      border: `${borders.borderWidth[1]} solid ${palette.divider}`,
                      backgroundColor: palette.background.default,
                    })}
                  >
                    <MDTypography variant='button' fontWeight='medium' color='text'>
                      {streamingProvider || '-'}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox mb={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bypassQueue}
                        onChange={(event) => setBypassQueue(event.target.checked)}
                        disabled={loading || saving}
                      />
                    }
                    label='Bypass Queue'
                  />
                </MDBox>

                <MDBox mb={3}>
                  <TextField
                    label='Map Type'
                    type='number'
                    value={mapType}
                    onChange={(event) => setMapType(event.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    fullWidth
                    size='small'
                    disabled={loading || saving}
                  />
                </MDBox>

                <MDBox display='flex' gap={1}>
                  <MDButton
                    variant='gradient'
                    color='info'
                    onClick={loadConfig}
                    disabled={loading || saving}
                  >
                    Refresh
                  </MDButton>
                  <MDButton
                    variant='gradient'
                    color='success'
                    onClick={onSave}
                    disabled={loading || saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
